'use client';

import { useEffect, useRef } from 'react';

import styles from './website-trust-sections.module.css';

const source = '/videos/global-connectivity-loop.mp4';
const poster = '/images/editorial/global-connectivity-poster.jpg';

export function WebsiteSecurityVideo() {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const section = video.closest('section') ?? video;
    let visible = false;
    let frame = 0;
    const updateParallax = () => {
      frame = 0;
      const bounds = section.getBoundingClientRect();
      const progress = Math.max(
        -1,
        Math.min(
          1,
          (window.innerHeight / 2 - bounds.top - bounds.height / 2) /
            ((window.innerHeight + bounds.height) / 2),
        ),
      );
      video.style.transform = motion.matches
        ? 'translate3d(0, 0, 0)'
        : `translate3d(0, ${progress * 24}px, 0)`;
    };
    const scheduleParallax = () => {
      if (!frame) frame = requestAnimationFrame(updateParallax);
    };
    const sync = () => {
      scheduleParallax();
      if (motion.matches || document.hidden || !visible) {
        video.pause();
        return;
      }
      if (!video.getAttribute('src')) video.src = source;
      void video.play().catch(() => {});
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry?.isIntersecting ?? false;
      sync();
    });
    observer.observe(section);
    window.addEventListener('scroll', scheduleParallax, { passive: true });
    window.addEventListener('resize', scheduleParallax);
    scheduleParallax();
    video.addEventListener('canplay', sync);
    motion.addEventListener('change', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleParallax);
      window.removeEventListener('resize', scheduleParallax);
      video.removeEventListener('canplay', sync);
      motion.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
      video.pause();
    };
  }, []);
  return (
    <video
      ref={ref}
      className={styles.auraVideo}
      style={{
        inset: '-32px 0',
        height: 'calc(100% + 64px)',
        display: 'block',
        transform: 'translate3d(0, 0, 0)',
      }}
      poster={poster}
      data-global-connectivity
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      aria-hidden="true"
      tabIndex={-1}
    />
  );
}
